import React from 'react';
import { Box } from 'native-base';
import { faAward, faCartShopping, faCoins, faCakeCandles, faUserCircle, faGift, faUserPlus, faHeart, faDollarSign, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { LinkExtension, observer, useAppSettings, useI18n, useSpringBigStore, useTheme } from '@springbig/shared';
import Image from '~/shared/Image';
import { SpIcon } from '~/shared/Icon';
import { Caption, H4 } from '~/shared/Typography';
import { AppRouteProps, useAppNavigate } from '~/pages';
import Pressable from '~/shared/Pressable';
import { useSendEvent } from '@springbig/shared';

import type { ExtensionTypes, TargetWalletTypes, NormalizedExtension } from '@springbig/shared';

type DisplayTypes = Exclude<ExtensionTypes, 'target_wallet'> | `target_wallet:${TargetWalletTypes}`;

export type MessageLinkBoxProps = {
    url: string;
    type: ExtensionTypes;
    walletType?: TargetWalletTypes;
    link?: LinkExtension;
    stashboardKey: string;
}

type Props = {
    stashboardKey: string;
    extension: NormalizedExtension;
}

type ExtensionProps = {
    caption: string;
    faIcon: IconDefinition;
    title: string | null;
}

const MessageExtensionBox = ({ extension, stashboardKey }: Props) => {

    const {
        button_primary,
        button_text,
        card,
        card_text,
        card_border_size,
        card_border_color,
        radius_md,
    } = useTheme();

    const { merchant, hasFeature } = useSpringBigStore();
    const { redirections } = useAppSettings();
    const { t } = useI18n();
    const { navigate } = useAppNavigate();
    const { mutate: sendEventRequest } = useSendEvent();
    const validImage = extension.thumbnail ? `${extension.thumbnail}?h=256&w=256` : null;
    const displayTypeMap: Record<DisplayTypes, ExtensionProps> = {
        'any_link': {
            caption: t('anylink.view_more'),
            faIcon: faGlobe,
            title: extension.title,
        },
        'digital_coupon': {
            caption: t('anylink.view_offers'),
            faIcon: faGift,
            title: 'Click here to see your new offer available',
        },
        'bonus_points': {
            caption: t('anylink.view_offers'),
            faIcon: faCoins,
            title: 'Click here to see your new bonus points offer available',
        },
        'gift_card': {
            caption: t('anylink.view_gift_card', [merchant?.gift_card_alias ?? 'gift card']),
            faIcon: faDollarSign,
            title: 'Click here to view offers',
        },
        'instant_points': {
            caption: t('anylink.view_rewards'),
            faIcon: faAward,
            title: 'Points have been added to you account! Click here to view rewards',
        },
        'target_wallet:birthday': {
            caption: t('anylink.view_info'),
            faIcon: faCakeCandles,
            title: 'Click here to make sure your birthday is up to date',
        },
        'target_wallet:home': {
            caption: t('anylink.view_rewards'),
            faIcon: faAward,
            title: 'Click here to view rewards',
        },
        'target_wallet:interests': {
            caption: t('anylink.view_interests'),
            faIcon: faHeart,
            title: 'Click here to tell us about you for a more personalized experience',
        },
        'target_wallet:profile': {
            caption: t('anylink.view_profile'),
            faIcon: faUserCircle,
            title: 'Click here to make sure your account is up to date',
        },
        'target_wallet:referral': {
            caption: t('anylink.view_refer'),
            faIcon: faUserPlus,
            title: 'Click here to refer a friend!',
        },
        'target_wallet:shop online': {
            caption: t('anylink.shop_online'),
            faIcon: faCartShopping,
            title: 'Click here to view our menu'
        }
    };

    let eventLabel = extension.title;
    let eventTarget = null;
    let displayType = extension.type as DisplayTypes;

    if (extension.type === 'any_link') {
        eventTarget = extension.url;
    }

    if (extension.type === 'target_wallet') {
        eventLabel = extension.wallet_type || 'unknown';

        if (extension.wallet_type) {
            displayType = `${extension.type}:${extension.wallet_type}`;
        }
    }

    const { caption, faIcon, title } = displayTypeMap[displayType] ?? {};

    const sendEvent = (event: Record<string, unknown>) => {

        if (!hasFeature('extension_actions_report')) {
            return;
        }


        sendEventRequest(event);
    };

    const redirectUrl = () => {

        sendEvent({
            event: 'click',
            sbk: stashboardKey,
            extension: extension.type,
            id: extension.id,
            label: eventLabel,
            target: eventTarget,
            source: 'wallet',
        });

        if (extension.type === 'any_link') {
            observer.emit('any-link-clicked', {
                type: extension.type || 'none',
                url: extension.url || '',
                _key: stashboardKey
            });

            window.open(extension.url, '_blank', 'noopener,noreferrer');
            return;
        }

        const targetUrl = new URL(extension.url);
        let fullPath = targetUrl.pathname;

        Object.entries(redirections).forEach(([key, value]) => {

            fullPath = fullPath.replace(key, value);
        });

        const pathWitoutLeadingSlash = fullPath.replace(/^\//, '');
        const route = `${pathWitoutLeadingSlash}${targetUrl.search}` as AppRouteProps;

        navigate(route);
    };

    return (
        <Box
            position='relative'
            justifyContent={'center'}
            p={2}
        >
            <Pressable
                borderRightRadius={8}
                borderLeftRadius={8}
                borderColor={card_border_color}
                borderWidth={card_border_size}
                rounded={radius_md && radius_md / 2 || 0}
                px={4}
                py={2}
                mr={3}
                flexDirection={'row'}
                color={card_text}
                bg={card}
                w={'56'}
                h={'20'}
                alignItems={'center'}
                onPress={redirectUrl}
            >
                <Box
                    position={'absolute'}
                    top={-8}
                    left={-8}
                    bg={button_primary}
                    p={1}
                    borderRadius={'100'}
                >
                    <SpIcon
                        color={button_text}
                        size={12}
                        icon={faIcon}
                    />
                </Box>
                <Box>
                    {
                        validImage ? <Image
                            rounded={radius_md && radius_md / 2 || 0}
                            w={'16'}
                            h={'16'}
                            uri={validImage}
                        /> : <Box>
                            <SpIcon
                                color={card_text}
                                size={54}
                                icon={faIcon}
                            />
                        </Box>
                    }
                </Box>
                <Box w={'32'} ml={2} h={'100%'} justifyContent='space-between'>
                    <H4 fontWeight={'normal'} fontSize={10}>
                        { title }
                    </H4>
                    <Caption textAlign={'right'} fontSize={11}>
                        { caption }
                    </Caption>
                </Box>
            </Pressable>
        </Box>
    );
};

export default MessageExtensionBox;
